import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { 
  ArrowLeft, Users, Building2, BarChart3, MessageSquare, 
  FileText, Sparkles, Map, GraduationCap, UserPlus, 
  RefreshCw, CheckCircle, AlertCircle, Clock, Eye,
  Lock, ChevronDown, ChevronUp, Loader2, ArrowRight
} from 'lucide-react';

function StatCard({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{label}</p>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-2xl font-outfit font-bold text-slate-900">{value}</p>
    </div>
  );
}

function CreateClientModal({ isOpen, onClose, onCreated, prefill }) {
  const [districtName, setDistrictName] = useState(prefill?.district || '');
  const [adminEmail, setAdminEmail] = useState(prefill?.email || '');
  const [adminName, setAdminName] = useState(prefill?.name || '');
  const [adminPassword, setAdminPassword] = useState('Welcome2026!');
  const [plan, setPlan] = useState('trial');
  const [campusText, setCampusText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (prefill) {
      setDistrictName(prefill.district || '');
      setAdminEmail(prefill.email || '');
      setAdminName(prefill.name || '');
    }
  }, [prefill]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);
    try {
      await api.post('/admin/create-client', {
        district_name: districtName,
        admin_email: adminEmail,
        admin_name: adminName,
        admin_password: adminPassword,
        plan,
        campuses: campusText.split('\n').map((s) => s.trim()).filter(Boolean),
      });
      setSuccess(`Client created! Email: ${adminEmail} / Password: ${adminPassword}`);
      onCreated();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-lg animate-in">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-outfit text-lg font-bold text-slate-900">Create Client Instance</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><span className="text-2xl text-slate-400">&times;</span></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2"><AlertCircle className="w-4 h-4" />{error}</div>}
          {success && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2"><CheckCircle className="w-4 h-4" />{success}</div>}
          
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">District Name *</label>
            <input type="text" value={districtName} onChange={(e) => setDistrictName(e.target.value)} required
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Springfield ISD" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Admin Name *</label>
              <input type="text" value={adminName} onChange={(e) => setAdminName(e.target.value)} required
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="John Smith" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Admin Email *</label>
              <input type="email" value={adminEmail} onChange={(e) => setAdminEmail(e.target.value)} required
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="admin@district.edu" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Initial Password *</label>
              <input type="text" value={adminPassword} onChange={(e) => setAdminPassword(e.target.value)} required
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Plan</label>
              <select value={plan} onChange={(e) => setPlan(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="trial">Trial (14 days)</option>
                <option value="standard">Standard (1 year)</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">Campuses <span className="normal-case font-normal">(optional, one per line)</span></label>
            <textarea value={campusText} onChange={(e) => setCampusText(e.target.value)} rows={3}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder={"Lincoln Elementary\nWashington Middle School\nCentral High School"} data-testid="client-campuses-input" />
          </div>
          <button type="submit" disabled={submitting}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating...</> : <><UserPlus className="w-4 h-4" /> Create Client</>}
          </button>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ isOpen, onClose }) {
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setResult('');
    setSubmitting(true);
    try {
      await api.post('/admin/reset-password', { email, new_password: newPassword });
      setResult(`Password reset for ${email}`);
      setEmail('');
      setNewPassword('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl w-full max-w-md animate-in">
        <div className="p-6 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-outfit text-lg font-bold text-slate-900">Reset User Password</h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg"><span className="text-2xl text-slate-400">&times;</span></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{error}</div>}
          {result && <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 px-4 py-3 rounded-lg text-sm">{result}</div>}
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">User Email</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="user@district.edu" />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-wider font-semibold text-slate-500 mb-1.5">New Password</label>
            <input type="text" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={6}
              className="w-full px-4 py-2.5 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="New password" />
          </div>
          <button type="submit" disabled={submitting}
            className="w-full bg-slate-900 text-white py-3 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50 transition-all flex items-center justify-center gap-2">
            {submitting ? 'Resetting...' : <><Lock className="w-4 h-4" /> Reset Password</>}
          </button>
        </form>
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [districts, setDistricts] = useState([]);
  const [demoRequests, setDemoRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showCreateClient, setShowCreateClient] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [clientPrefill, setClientPrefill] = useState(null);
  const [converting, setConverting] = useState(null);

  const fetchData = async () => {
    try {
      const [analyticsRes, districtsRes, demosRes] = await Promise.all([
        api.get('/admin/analytics'),
        api.get('/admin/districts'),
        api.get('/admin/demo-requests'),
      ]);
      setAnalytics(analyticsRes.data);
      setDistricts(districtsRes.data.districts || []);
      setDemoRequests(demosRes.data.demo_requests || []);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleConvertDemo = async (demo) => {
    setConverting(demo._id);
    try {
      const res = await api.post(`/admin/convert-demo/${demo._id}`, { password: 'Welcome2026!' });
      alert(`Account created!\n\nEmail: ${res.data.admin_email}\nPassword: ${res.data.password}\nDistrict: ${res.data.district_name}`);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to convert');
    } finally {
      setConverting(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading admin dashboard...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'districts', label: 'Districts', icon: Building2 },
    { id: 'demos', label: 'Demo Requests', icon: FileText },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="p-2 hover:bg-slate-100 rounded-lg transition-all">
              <ArrowLeft className="w-5 h-5 text-slate-500" />
            </Link>
            <div>
              <h1 className="font-outfit text-xl font-bold text-slate-900">Admin Dashboard</h1>
              <p className="text-sm text-slate-500">Platform management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowResetPassword(true)}
              className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg font-medium transition-all flex items-center gap-2 text-sm">
              <Lock className="w-4 h-4" /> Reset Password
            </button>
            <button onClick={() => { setClientPrefill(null); setShowCreateClient(true); }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-all flex items-center gap-2 text-sm" data-testid="create-client-btn">
              <UserPlus className="w-4 h-4" /> New Client
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-1 bg-white border border-slate-200 rounded-xl p-1 mb-6">
          {tabs.map((tab) => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
                activeTab === tab.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-50'
              }`}>
              <tab.icon className="w-4 h-4" />{tab.label}
              {tab.id === 'demos' && analytics?.totals?.pending_demos > 0 && (
                <span className="bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{analytics.totals.pending_demos}</span>
              )}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div className="space-y-6 animate-in">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Total Users" value={analytics.totals.users} icon={Users} color="text-blue-500" />
              <StatCard label="Districts" value={analytics.totals.districts} icon={Building2} color="text-emerald-500" />
              <StatCard label="Assessments" value={analytics.totals.assessments} icon={Sparkles} color="text-purple-500" />
              <StatCard label="Scorecards" value={analytics.totals.scorecards} icon={BarChart3} color="text-orange-500" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard label="Roadmaps" value={analytics.totals.roadmaps} icon={Map} color="text-violet-500" />
              <StatCard label="Chat Sessions" value={analytics.totals.chat_sessions} icon={MessageSquare} color="text-teal-500" />
              <StatCard label="Risk Reports" value={analytics.totals.risk_reports} icon={AlertCircle} color="text-red-500" />
              <StatCard label="In Training" value={analytics.totals.users_in_training} icon={GraduationCap} color="text-amber-500" />
            </div>

            {/* Recent Activity */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-outfit text-lg font-semibold text-slate-900 mb-4">Last 7 Days</h3>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center">
                  <p className="text-3xl font-outfit font-bold text-blue-600">{analytics.recent_7d.new_users}</p>
                  <p className="text-sm text-slate-500">New Users</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-outfit font-bold text-purple-600">{analytics.recent_7d.assessments}</p>
                  <p className="text-sm text-slate-500">Assessments</p>
                </div>
                <div className="text-center">
                  <p className="text-3xl font-outfit font-bold text-teal-600">{analytics.recent_7d.chat_sessions}</p>
                  <p className="text-sm text-slate-500">AI Chats</p>
                </div>
              </div>
            </div>

            {/* Sales Assets */}
            <div className="bg-slate-900 text-white rounded-xl p-6" data-testid="sales-assets">
              <h3 className="font-outfit text-lg font-semibold mb-1">Sales Assets</h3>
              <p className="text-sm text-slate-400 mb-4">Ready-to-send materials for district and risk-pool conversations.</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  ['Demo Deck (PowerPoint)', '/ERIC_Demo_Deck.pptx', '19 slides · editable', 'demo-deck-pptx'],
                  ['Demo Deck (PDF)', '/ERIC_Demo_Deck.pdf', '19 slides · share-ready', 'demo-deck-pdf'],
                  ['Executive Overview (PDF)', '/ERIC_Executive_Overview.pdf', '6 pages · leave-behind', 'exec-overview-pdf'],
                ].map(([label, href, meta, tid]) => (
                  <a key={href} href={href} download className="bg-white/5 hover:bg-white/10 rounded-lg p-4 flex items-start gap-3 transition-all" data-testid={tid}>
                    <FileText className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-sm">{label}</p>
                      <p className="text-xs text-slate-400">{meta}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>

            {/* Demo Requests Alert */}
            {analytics.totals.pending_demos > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-amber-600" />
                  <span className="text-amber-800 font-medium">{analytics.totals.pending_demos} pending demo request(s)</span>
                </div>
                <button onClick={() => setActiveTab('demos')} className="text-amber-700 font-medium text-sm hover:underline flex items-center gap-1">
                  View <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Districts Tab */}
        {activeTab === 'districts' && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden animate-in">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-outfit text-lg font-semibold text-slate-900">All Districts ({districts.length})</h3>
            </div>
            {districts.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No districts yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {districts.map((district) => (
                  <div key={district._id} className="px-6 py-4 flex items-center justify-between hover:bg-slate-50">
                    <div>
                      <p className="font-medium text-slate-900">{district.name}</p>
                      <p className="text-sm text-slate-500">
                        {district.user_count} user(s) | Plan: {district.subscription?.plan || 'trial'} | 
                        Expires: {district.subscription?.expires_at ? new Date(district.subscription.expires_at).toLocaleDateString() : 'N/A'}
                      </p>
                    </div>
                    <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                      district.subscription?.plan === 'standard' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'
                    }`}>
                      {district.subscription?.plan || 'trial'}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Demo Requests Tab */}
        {activeTab === 'demos' && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden animate-in">
            <div className="px-6 py-4 border-b border-slate-200">
              <h3 className="font-outfit text-lg font-semibold text-slate-900">Demo Requests ({demoRequests.length})</h3>
            </div>
            {demoRequests.length === 0 ? (
              <div className="p-8 text-center text-slate-500">No demo requests yet.</div>
            ) : (
              <div className="divide-y divide-slate-100">
                {demoRequests.map((demo) => (
                  <div key={demo._id} className="px-6 py-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-slate-900">{demo.name}</p>
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            demo.status === 'converted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                          }`}>
                            {demo.status === 'converted' ? 'Converted' : 'New'}
                          </span>
                        </div>
                        <p className="text-sm text-slate-500">{demo.email} | {demo.district} | {demo.role || 'No role'}</p>
                        {demo.phone && <p className="text-sm text-slate-400">{demo.phone}</p>}
                        {demo.message && <p className="text-sm text-slate-500 mt-1 italic">"{demo.message}"</p>}
                        <p className="text-xs text-slate-400 mt-1">{demo.created_at ? new Date(demo.created_at).toLocaleString() : ''}</p>
                      </div>
                      {demo.status !== 'converted' && (
                        <button
                          onClick={() => handleConvertDemo(demo)}
                          disabled={converting === demo._id}
                          className="px-3 py-2 bg-blue-600 text-white text-sm rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 transition-all flex items-center gap-1.5 flex-shrink-0"
                        >
                          {converting === demo._id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UserPlus className="w-3.5 h-3.5" />}
                          Create Account
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      <CreateClientModal
        isOpen={showCreateClient}
        onClose={() => setShowCreateClient(false)}
        onCreated={() => { fetchData(); }}
        prefill={clientPrefill}
      />
      <ResetPasswordModal
        isOpen={showResetPassword}
        onClose={() => setShowResetPassword(false)}
      />
    </div>
  );
}
